import React, { memo, useMemo, useRef } from "react";
import { Container } from "@pixi/react";
import { Settings, GAME_LAYOUT } from "../../../settings";
import FreeSpinBasicOverlayReel from "../FreeSpinBasicOverlayReel/FreeSpinBasicOverlayReel";
import FreeSpinBasicOverlaySymbol from "../FreeSpinBasicOverlaySymbol/FreeSpinBasicOverlaySymbol";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";

/**
 * FLAT overlay container for the Mad Labs bonus board.
 *
 * A copy of the base game's OverlayReelSet, on its own ref array. See
 * FreeSpinBasicReelSet for why this is a copy and not the same component.
 *
 * All 15 symbols (5 reels × 3 rows) are siblings in ONE sortable container, so
 * a symbol on reel 1 can render above one on reel 2 — impossible while each
 * reel owned its own 3 children, because PIXI only sorts among siblings.
 *
 * FreeSpinBasicOverlayReel still exists per reel: it owns that reel's
 * FreeSpinBasicOverlayReelController and hosts the full-reel expanding wild
 * spine. It just doesn't parent the symbols anymore — it receives them as a
 * ref slice.
 *
 * No alpha gate: the bonus scene shows and hides this board.
 */
const FreeSpinBasicOverlayReelSet = memo(({ symbolWidth, symbolHeight, reelSpacing }) => {
  const { freeSpinBasicOverlayReelRefs: overlayReelRefs } = useReelRefs();
  const symbolRefs = useRef(
    Array.from(
      { length: GAME_LAYOUT.REEL_COUNT },
      () => new Array(GAME_LAYOUT.SYMBOLS_VISIBLE),
    ),
  );

  // Memoize container Y position calculation
  const containerY = useMemo(
    () => -symbolHeight * (0.5 * Settings.SYMBOLS_VISIBLE - 0.5),
    [symbolHeight]
  );

  // Pre-compute all 15 cell positions. x used to come from the reel container;
  // each symbol carries its own now. Both recompute on resize because they're
  // plain props derived from symbolHeight/reelSpacing.
  const symbolPositions = useMemo(() => {
    const positions = [];
    const centerOffset = (GAME_LAYOUT.REEL_COUNT - 1) / 2;
    for (let ri = 0; ri < GAME_LAYOUT.REEL_COUNT; ri++) {
      for (let si = 0; si < GAME_LAYOUT.SYMBOLS_VISIBLE; si++) {
        positions.push({
          reelIndex: ri,
          symbolIndex: si,
          x: reelSpacing * (ri - centerOffset),
          y: symbolHeight * si,
        });
      }
    }
    return positions;
  }, [reelSpacing, symbolHeight]);

  return (
    <Container
      name="fs_spine_overlay"
      zIndex={5}
      y={containerY}
      sortableChildren
    >
      {/* The 15 sortable cells. Emitted before the reels so their refs are
          attached by the time a reel's effect builds its controller. */}
      {symbolPositions.map(({ reelIndex, symbolIndex, x, y }) => (
        <FreeSpinBasicOverlaySymbol
          key={`${reelIndex}_${symbolIndex}`}
          index={symbolIndex}
          reelIndex={reelIndex}
          x={x}
          y={y}
          ref={(el) => {
            symbolRefs.current[reelIndex][symbolIndex] = el;
          }}
        />
      ))}

      {/* Controller hosts — one per reel, carrying only the expanding wild. */}
      {GAME_LAYOUT.REEL_INDICES.map((index) => (
        <FreeSpinBasicOverlayReel
          key={index}
          symbolHeight={symbolHeight}
          symbolWidth={symbolWidth}
          reelSpacing={reelSpacing}
          symbolRefs={symbolRefs.current[index]}
          ref={(el) => {
            overlayReelRefs.current[index] = el;
          }}
          index={index}
        />
      ))}
    </Container>
  );
});

export default FreeSpinBasicOverlayReelSet;
